import { Box, Flex, Text, Tooltip, useColorModeValue } from '@chakra-ui/react';
import { RefObject } from 'react';
import { EVENT_COLORS, GameEvent, formatDuration } from '../types';
import { translate } from '../../../i18n';

interface Props {
  events: GameEvent[];
  duration: number;
  videoRef: RefObject<HTMLVideoElement>;
}

export default function EventTimeline({ events, duration, videoRef }: Props) {
  const trackBg = useColorModeValue('gray.200', 'whiteAlpha.200');

  const seekTo = (time: number) => {
    if (videoRef.current) {
      videoRef.current.currentTime = time;
      videoRef.current.play().catch(() => {});
    }
  };

  return (
    <Box mt={3}>
      <Text fontSize="xs" color="secondaryGray.600" mb={1}>{translate('court_recording.event_timeline')}</Text>
      <Box position="relative" h="24px" bg={trackBg} borderRadius="12px" overflow="hidden">
        {events.map((event, i) => {
          const pct = duration > 0 ? (event.time / duration) * 100 : 0;
          const isShot = event.type === 'shot';
          return (
            <Tooltip
              key={i}
              label={`${event.type.replace('_', ' ')} @ ${event.time.toFixed(1)}s${event.angle_change ? ` (${event.angle_change.toFixed(0)}°)` : ''}`}
              fontSize="xs"
            >
              <Box
                position="absolute"
                left={`${pct}%`}
                top="50%"
                transform="translate(-50%, -50%)"
                w={isShot ? '10px' : '3px'}
                h={isShot ? '10px' : '16px'}
                borderRadius={isShot ? '50%' : '1px'}
                bg={EVENT_COLORS[event.type] || 'gray.400'}
                cursor="pointer"
                _hover={{ transform: 'translate(-50%, -50%) scale(1.5)' }}
                transition="transform 0.15s"
                onClick={() => seekTo(event.time)}
              />
            </Tooltip>
          );
        })}
      </Box>
      <Flex justify="space-between" mt={1}>
        <Text fontSize="xs" color="secondaryGray.600">0:00</Text>
        <Flex gap={3}>
          {[
            { label: translate('court_recording.shot'), color: EVENT_COLORS.shot },
            { label: translate('court_recording.rally_start'), color: EVENT_COLORS.rally_start },
            { label: translate('court_recording.rally_end'), color: EVENT_COLORS.rally_end },
          ].map((item) => (
            <Flex key={item.label} align="center" gap={1}>
              <Box w="8px" h="8px" borderRadius="50%" bg={item.color} />
              <Text fontSize="xs" color="secondaryGray.600">{item.label}</Text>
            </Flex>
          ))}
        </Flex>
        <Text fontSize="xs" color="secondaryGray.600">
          {formatDuration(duration)}
        </Text>
      </Flex>
    </Box>
  );
}
